import type {StickyPlacement, StickyStackItem, StickyStackState, StickyType} from 'app/utils/stickyStack/interface';

import {stickyHeights} from './stickyHeights';
import type {AddStickyStackItemParams, SetStickyStackItemActiveParams, SetStickyStackVisibleOnlyParams} from './types';

const orderByStickyType: Record<StickyType, number> = {
    DESKTOP_HEADER: 0,
    MOBILE_HEADER: 0,
    PWA_HEADER: 0,
    IN_PG_NAV: 10,
    FILTERS: 20,
    OTHER: 30,
    LAST: 40,
    POPUP: 45,
    STICKY_LINKS: 50,
    AUDIO_PLAYER: 60,
    COMPARISON: 70,
    TABLE_STICKY_HEADER: 80,
};

const allowMultipleInlineByStickyType: Record<StickyType, boolean> = {
    DESKTOP_HEADER: false,
    MOBILE_HEADER: false,
    PWA_HEADER: false,
    IN_PG_NAV: false,
    FILTERS: false,
    OTHER: false,
    LAST: false,
    POPUP: false,
    STICKY_LINKS: false,
    AUDIO_PLAYER: false,
    COMPARISON: false,
    TABLE_STICKY_HEADER: true,
};

export const addStickyStackItemToState = (state: StickyStackState, {cId, stickyType, height}: AddStickyStackItemParams): StickyStackState => {
    const placement = getPlacement(stickyType);

    if (state.find((item) => item.cId === cId)) {
        console.warn(`Sticky cId '${cId}' is not unique!`);
        return state;
    }

    const items = [
        ...state,
        {
            cId,
            stickyType,
            placement,
            displayMultipleActiveInline: allowMultipleInlineByStickyType[stickyType],
            height: getHeight(stickyType, height),
            active: false,
            order: getOrder(state, stickyType),
            top: 0,
            bottom: 0,
            visible: true,
        },
    ];

    return recalculateStickyStack(sortItemsAsc(items));
};

export const removeStickyStackItemFromState = (state: StickyStackState, cId: string): StickyStackState => {
    const items = state.filter((item) => item.cId !== cId);
    return recalculateStickyStack(items);
};

export const setStickyStackItemActiveInState = (state: StickyStackState, {cId, active}: SetStickyStackItemActiveParams): StickyStackState => {
    const items = state.map((item) => {
        if (item.cId !== cId) {
            return item;
        }

        return {
            ...item,
            active,
        };
    });

    return recalculateStickyStack(items);
};

export const setStickyStackVisibleOnlyInState = (state: StickyStackState, {cId, visible}: SetStickyStackVisibleOnlyParams): StickyStackState => {
    let items = state;

    if (visible) {
        items = state.map((item) => {
            if (item.cId === cId) {
                return {
                    ...item,
                    visible,
                };
            }

            return {
                ...item,
                visible: false,
            };
        });
    }

    if (!visible) {
        items = state.map((item) => {
            return {
                ...item,
                visible: true,
            };
        });
    }

    return recalculateStickyStack(items);
};

export const recalculateStickyStackHeightInState = (state: StickyStackState): StickyStackState => {
    const items = state.map((item) => ({
        ...item,
        height: getHeight(item.stickyType, item.height, item.cId),
    }));

    return recalculateStickyStack(items);
};

function sortItemsAsc(state: StickyStackState): StickyStackState {
    return [...state].sort((itemA: StickyStackItem, itemB: StickyStackItem) => itemA.order - itemB.order);
}

function sortItemsDesc(state: StickyStackState): StickyStackState {
    return [...state].sort((itemA: StickyStackItem, itemB: StickyStackItem) => itemB.order - itemA.order);
}

function getOrder(state: StickyStackState, stickyType: StickyType): number {
    const sameItemTypes = state.filter((item) => item.stickyType === stickyType);
    const lastItem = sameItemTypes.pop();
    if (lastItem) {
        return lastItem.order + 1;
    }

    return orderByStickyType[stickyType];
}

function getHeight(type: StickyType, height?: number, cId?: string): number {
    switch (type) {
        case 'LAST':
        case 'OTHER':
            if (!height) {
                throw new Error(`Height for sticky type ${type} must be > 0!`);
            }
            return height;
        default:
            return stickyHeights[type](cId);
    }
}

function getPlacement(type: StickyType): StickyPlacement {
    switch (type) {
        case 'DESKTOP_HEADER':
        case 'MOBILE_HEADER':
        case 'IN_PG_NAV':
        case 'FILTERS':
        case 'LAST':
        case 'OTHER':
        case 'TABLE_STICKY_HEADER':
        case 'PWA_HEADER':
            return 'TOP';
        case 'POPUP':
        case 'STICKY_LINKS':
        case 'AUDIO_PLAYER':
        case 'COMPARISON':
            return 'BOTTOM';
    }
}

function recalculateStickyStack(state: StickyStackState): StickyStackState {
    let previousTop = 0;
    let previousBottom = 0;

    const items = state.map((item) => ({
        ...item,
        height: getHeight(item.stickyType, item.height, item.cId),
    }));

    const itemsWithBottom = sortItemsDesc(items).map((item) => {
        if (!item.active || item.placement !== 'BOTTOM') {
            return item;
        }

        const itemWithBottom = {
            ...item,
            bottom: previousBottom,
        };

        previousBottom += item.height;
        return itemWithBottom;
    });

    const stickyTypeToTop = new Map<StickyType, number>();

    const itemsWithTop = sortItemsAsc(itemsWithBottom).map((item) => {
        if (!item.active || item.placement !== 'TOP') {
            return item;
        }

        let top = previousTop;
        const increasePreviousTop = () => {
            previousTop += item.height;
        };

        if (item.displayMultipleActiveInline) {
            const existingTop = stickyTypeToTop.get(item.stickyType);
            if (typeof existingTop === 'number') {
                top = existingTop;
            } else {
                stickyTypeToTop.set(item.stickyType, top);
                increasePreviousTop();
            }
        } else {
            increasePreviousTop();
        }

        return {
            ...item,
            top,
        };
    });

    return itemsWithTop.map((item) => {
        if (item.active) {
            return item;
        }

        if (item.placement === 'TOP') {
            return {
                ...item,
                top: previousTop,
            };
        }

        return {
            ...item,
            bottom: previousBottom,
        };
    });
}
